import type { ClientCascadeLockPreview } from "api-types/homePlans/homePlanResponses";
import type { HomePlanStatus } from "api-types/homePlans/homePlanTypes";
import { lotStatusAllowsUnitLock } from "api-types/lots/lotTypes";
import type { LotStatus } from "api-types/lots/lotTypes";

/**
 * Whether a Home Plan has progressed far enough to lock.
 * Building aggregates (isMultiUnit) use planStatus like single-family plans.
 * Unit Home Plans at multi-unit locations stay in PREP; lot status tracks phase instead.
 */
export function homePlanLockableByPhase(params: {
  planStatus: HomePlanStatus;
  isMultiUnit: boolean;
  atMultiUnitLocation: boolean;
  lotStatus?: LotStatus;
}): boolean {
  if (params.isMultiUnit) {
    return params.planStatus !== "PREP";
  }
  if (params.atMultiUnitLocation) {
    return lotStatusAllowsUnitLock(params.lotStatus);
  }
  return params.planStatus !== "PREP";
}

export function homePlanLockableForSubmit(params: {
  planStatus: HomePlanStatus;
  isMultiUnit: boolean;
  atMultiUnitLocation: boolean;
  lotStatus?: LotStatus;
  cascadeLockPreview?: Pick<ClientCascadeLockPreview, "lockable">;
}): boolean {
  return homePlanLockableByPhase(params) && (!params.isMultiUnit || params.cascadeLockPreview?.lockable === true);
}

/** True when a unit home plan was locked in the same cascade as its building aggregate. */
export function unitLockMatchesAggregateLock(
  unitLockedAt: Date | null | undefined,
  aggregateLockedAt: Date | null | undefined,
): boolean {
  if (
    unitLockedAt === null ||
    unitLockedAt === undefined ||
    aggregateLockedAt === null ||
    aggregateLockedAt === undefined
  ) {
    return false;
  }
  return unitLockedAt.getTime() === aggregateLockedAt.getTime();
}
